import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, MoreVertical, Package, Eye, Settings, Tags, Trash2, ArrowRight } from "lucide-react";
import { Application } from "@/types/application";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useLongPress } from "@/hooks/useLongPress";
import { MobileRowActionsSheet } from "@/components/ui/mobile-row-actions-sheet";
import { ConfigureApplicationDialog } from "./ConfigureApplicationDialog";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";

interface MobileApplicationListProps {
  applications: Application[];
  searchQuery?: string;
  onUpdateApplication?: (app: Application) => void;
  onRemoveApplication?: (id: string) => void;
}

interface RowProps {
  app: Application;
  onOpenActions: (app: Application) => void;
}

function AppRow({ app, onOpenActions }: RowProps) {
  const navigate = useNavigate();
  const isPublished = String(app.status ?? "").toLowerCase() === "published";
  const platformLabel = Array.isArray(app.platform) ? app.platform.join(" · ") : (app.platform ?? "");
  const subtitle = [platformLabel, app.bundleId].filter(Boolean).join(" · ");
  const { handlers, didFireRef } = useLongPress(() => onOpenActions(app));

  return (
    <li>
      <div
        className={cn(
          "group flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left",
          "transition-colors hover:bg-accent/40 active:bg-accent/60",
          "min-h-[64px]"
        )}
      >
        <button
          type="button"
          {...handlers}
          onClick={() => {
            if (didFireRef.current) {
              didFireRef.current = false;
              return;
            }
            navigate(`/applications/${app.id}`);
          }}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-label={`Open ${app.name}. Long-press for actions.`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
            {app.icon ? (
              <img src={app.icon} alt={app.name} className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <Package className="h-5 w-5 text-muted-foreground" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium text-foreground">{app.name}</p>
              <span
                className={cn("h-1.5 w-1.5 shrink-0 rounded-full", isPublished ? "bg-success" : "bg-warning")}
                aria-label={isPublished ? "Published" : "Draft"}
                title={isPublished ? "Published" : "Draft"}
              />
            </div>
            {subtitle && <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" aria-hidden />
        </button>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={`Actions for ${app.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenActions(app);
          }}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

/**
 * Phone-optimized application list with a long-press / kebab → bottom-sheet
 * actions menu mirroring the desktop ApplicationList row actions.
 */
export function MobileApplicationList({
  applications,
  searchQuery,
  onUpdateApplication,
  onRemoveApplication,
}: MobileApplicationListProps) {
  const navigate = useNavigate();
  const [sheetApp, setSheetApp] = useState<Application | null>(null);
  const [configAppId, setConfigAppId] = useState<string | null>(null);
  const [tagAppId, setTagAppId] = useState<string | null>(null);
  const [deleteAppId, setDeleteAppId] = useState<string | null>(null);

  if (applications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Package className="mb-3 h-10 w-10 text-muted-foreground/60" aria-hidden />
        <p className="text-sm font-medium text-foreground">No applications match</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {searchQuery ? "Try a different search term." : "Add an application to get started."}
        </p>
      </div>
    );
  }

  const configApp = applications.find((a) => a.id === configAppId) || null;
  const tagApp = applications.find((a) => a.id === tagAppId) || null;

  const actionsFor = (app: Application) => [
    { label: "Publish", icon: ArrowRight, onClick: () => toast.success("Application published") },
    { label: "View Details", icon: Eye, onClick: () => navigate(`/applications/${app.id}`) },
    { label: "Assign Tags", icon: Tags, onClick: () => setTagAppId(app.id) },
    { label: "Configure", icon: Settings, onClick: () => setConfigAppId(app.id) },
    { label: "Delete", icon: Trash2, onClick: () => setDeleteAppId(app.id), destructive: true },
  ];

  return (
    <>
      <ul className="space-y-2">
        {applications.map((app) => (
          <AppRow key={app.id} app={app} onOpenActions={setSheetApp} />
        ))}
      </ul>

      {sheetApp && (
        <MobileRowActionsSheet
          open={!!sheetApp}
          onOpenChange={(open) => !open && setSheetApp(null)}
          title={sheetApp.name}
          subtitle={sheetApp.bundleId}
          actions={actionsFor(sheetApp)}
        />
      )}

      {configApp && (
        <ConfigureApplicationDialog
          application={configApp}
          open={!!configAppId}
          onOpenChange={(open) => !open && setConfigAppId(null)}
          onSave={(updated) => {
            onUpdateApplication?.(updated);
            setConfigAppId(null);
          }}
        />
      )}

      {tagApp && (
        <TagAssignmentDialog
          open={!!tagAppId}
          onOpenChange={(open) => !open && setTagAppId(null)}
          resourceId={tagApp.id}
          resourceType="application"
          resourceName={tagApp.name}
          onAssignmentComplete={() => {
            toast.success("Tags assigned successfully");
            setTagAppId(null);
          }}
        />
      )}

      <AlertDialog open={!!deleteAppId} onOpenChange={(open) => !open && setDeleteAppId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this application? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteAppId) onRemoveApplication?.(deleteAppId);
                setDeleteAppId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default MobileApplicationList;
